import { isAnalyticsAllowed } from "@/lib/consent";

export type AnalyticsDeviceType = "mobile" | "tablet" | "desktop";
export type AnalyticsProperty =
  string | number | boolean | readonly string[] | undefined;
export type AnalyticsProperties = Readonly<Record<string, AnalyticsProperty>>;

type AnalyticsScope = "landing" | "dashboard";

interface EmitAnalyticsEventOptions {
  eventType: string;
  section: string;
  scope: AnalyticsScope;
  properties?: AnalyticsProperties;
}

const SESSION_KEYS: Record<AnalyticsScope, string> = {
  landing: "btfy_landing_session",
  dashboard: "btfy_dashboard_session",
};

function getDeviceType(): AnalyticsDeviceType {
  if (window.innerWidth < 640) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
}

function createSessionId(): string {
  return typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSessionId(scope: AnalyticsScope): string {
  const key = SESSION_KEYS[scope];

  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;

    const sessionId = createSessionId();
    window.sessionStorage.setItem(key, sessionId);
    return sessionId;
  } catch {
    // Analytics must never disrupt the product when storage is unavailable.
    return createSessionId();
  }
}

/**
 * Consent-aware transport shared by public-site and authenticated-dashboard
 * analytics. Required envelope fields are applied last so callers cannot
 * accidentally replace them with arbitrary values.
 */
export function emitAnalyticsEvent({
  eventType,
  section,
  scope,
  properties = {},
}: EmitAnalyticsEventOptions): void {
  if (typeof window === "undefined" || !isAnalyticsAllowed()) return;

  const detail = {
    ...properties,
    event: eventType,
    event_type: eventType,
    section,
    device_type: getDeviceType(),
    session_id: getSessionId(scope),
    timestamp: new Date().toISOString(),
  };

  window.dispatchEvent(new CustomEvent("btfy:analytics", { detail }));

  const analyticsWindow = window as typeof window & {
    dataLayer?: Array<Record<string, unknown>>;
  };
  analyticsWindow.dataLayer?.push(detail);
}
